import { apiClient } from './client';
import type {
  Company,
  CompanyListResponse,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  UpdateQuotaPlanRequest,
  QuotaSummary,
  QuotaHistoryResponse,
  AllCompaniesQuotaResponse,
  CompanyUsersResponse,
  ReassignUserCompanyRequest,
  ReassignUserCompanyResponse,
  CompanyMembersResponse,
  AddMembershipRequest,
  AddMembershipResponse,
  UserMembershipsResponse,
} from './types';

export async function listCompanies(): Promise<CompanyListResponse> {
  return apiClient.get<CompanyListResponse>('/api/companies');
}

export async function getCompany(companyId: string): Promise<Company> {
  return apiClient.get<Company>(`/api/companies/${companyId}`);
}

export async function createCompany(data: CreateCompanyRequest): Promise<Company> {
  return apiClient.post<Company>('/api/companies', data);
}

export async function updateCompany(companyId: string, data: UpdateCompanyRequest): Promise<Company> {
  return apiClient.put<Company>(`/api/companies/${companyId}`, data);
}

export async function deleteCompany(companyId: string): Promise<void> {
  await apiClient.delete(`/api/companies/${companyId}`);
}

// ── Quota API ──

export async function updateQuotaPlan(data: UpdateQuotaPlanRequest): Promise<Company> {
  return apiClient.put<Company>('/api/quota/plan', data);
}

export async function getCurrentQuota(companyId?: string): Promise<QuotaSummary> {
  const url = companyId ? `/api/quota/current?companyId=${companyId}` : '/api/quota/current';
  return apiClient.get<QuotaSummary>(url);
}

export async function getQuotaHistory(companyId?: string): Promise<QuotaHistoryResponse> {
  const url = companyId ? `/api/quota/history?companyId=${companyId}` : '/api/quota/history';
  return apiClient.get<QuotaHistoryResponse>(url);
}

export async function getAllCompaniesQuota(): Promise<AllCompaniesQuotaResponse> {
  return apiClient.get<AllCompaniesQuotaResponse>('/api/quota/companies');
}

export async function getCompanyUsers(companyId: string): Promise<CompanyUsersResponse> {
  return apiClient.get<CompanyUsersResponse>(`/api/companies/${companyId}/users`);
}

export async function reassignUserCompany(
  cognitoSub: string,
  data: ReassignUserCompanyRequest,
): Promise<ReassignUserCompanyResponse> {
  return apiClient.put<ReassignUserCompanyResponse>(`/api/users/${cognitoSub}/company`, data);
}

// ── Membership API (Phase 3 M:N) ──

export async function listCompanyMembers(companyId: string): Promise<CompanyMembersResponse> {
  return apiClient.get<CompanyMembersResponse>(`/api/memberships/company/${companyId}`);
}

export async function addMembership(data: AddMembershipRequest): Promise<AddMembershipResponse> {
  return apiClient.post<AddMembershipResponse>('/api/memberships', data);
}

export async function removeMembership(companyId: string, email: string): Promise<void> {
  await apiClient.delete(`/api/memberships/${companyId}/${encodeURIComponent(email)}`);
}

export async function getUserMemberships(): Promise<UserMembershipsResponse> {
  return apiClient.get<UserMembershipsResponse>('/api/users/me/memberships');
}
